import {CardName} from "./api/CardName";
import {Card} from "./Card";
import {VideoPokerCardCombination} from "./VideoPokerCardCombination";
import {VideoPokerCardCombinationName} from "./VideoPokerCardCombinationName";

export class CardCombinationDetector {

    public static detect(cards: Array<Card>): VideoPokerCardCombination {
        if (cards.length !== 5) {
            throw new Error("Wrong amount of cards send!");
        }

        let combinationName: VideoPokerCardCombinationName = VideoPokerCardCombinationName.NOTHING;
        if (this.isJacksOrBetter(cards)) {
            combinationName = VideoPokerCardCombinationName.JACKS_OR_BETTER;
        }
        if (this.isTwoPairs(cards)) {
            combinationName = VideoPokerCardCombinationName.TWO_PAIRS;
        }
        if (this.isThreeOfKind(cards)) {
            combinationName = VideoPokerCardCombinationName.THREE_OF_A_KIND;
        }
        if (this.isStraight(cards)) {
            combinationName = VideoPokerCardCombinationName.STRAIGHT;
        }
        if (this.isFlush(cards)) {
            combinationName = VideoPokerCardCombinationName.FLUSH;
        }
        if (this.isFullHouse(cards)) {
            combinationName = VideoPokerCardCombinationName.FULL_HOUSE;
        }
        if (this.isFourOfAKind(cards)) {
            combinationName = VideoPokerCardCombinationName.FOUR_OF_A_KIND;
        }
        if (this.isStraightFlush(cards)) {
            combinationName = VideoPokerCardCombinationName.STRAIGHT_FLUSH;
        }
        if (this.isRoyalFlush(cards)) {
            combinationName = VideoPokerCardCombinationName.ROYAL_FLUSH;
        }

        return new VideoPokerCardCombination(combinationName, this.getCombinationPosition(cards, combinationName));
    }

    private static isJacksOrBetter(cards: Array<Card>): boolean {
        for (let i = 0; i < cards.length; i++) {
            for (let j = 0; j < cards.length; j++) {
                if (i !== j &&
                    cards[i].getName() === cards[j].getName() &&
                    (
                        cards[j].getName() === CardName.JACK ||
                        cards[j].getName() === CardName.QUEEN ||
                        cards[j].getName() === CardName.KING ||
                        cards[j].getName() === CardName.ACE)) {
                    return true;
                }
            }
        }
        return false;
    }

    private static isTwoPairs(cards: Array<Card>): boolean {
        const positionIndexes: Array<number> = [];
        for (let i = 0; i < cards.length; i++) {
            for (let j = 0; j < cards.length; j++) {
                if (i !== j &&
                    cards[i].getName() === cards[j].getName()) {
                    positionIndexes.push(i);
                    positionIndexes.push(j);
                    break;
                }
            }
            if (positionIndexes.length === 2) {
                break;
            }
        }

        for (let i = 0; i < cards.length; i++) {
            for (let j = 0; j < cards.length; j++) {
                if (i !== j &&
                    cards[i].getName() === cards[j].getName() &&
                    i !== positionIndexes[0] &&
                    i !== positionIndexes[1] &&
                    j !== positionIndexes[0] &&
                    j !== positionIndexes[1]) {
                    positionIndexes.push(i);
                    positionIndexes.push(j);
                    break;
                }
            }
            if (positionIndexes.length === 4) {
                break;
            }
        }

        return positionIndexes.length === 4;

    }

    private static isThreeOfKind(cards: Array<Card>): boolean {
        let sameCardsAmount = 1;
        for (let i = 0; i < cards.length; i++) {
            for (let j = 0; j < cards.length; j++) {
                if (cards[i].getName() === cards[j].getName() && i !== j) {
                    sameCardsAmount++;
                }

                if (sameCardsAmount === 3) {
                    return true;
                }
            }
            sameCardsAmount = 1;
        }
        return false;
    }

    private static isFullHouse(cards: Array<Card>): boolean {
        let threeOfKindPositions: Array<number> = [];
        const otherTwoCards: Array<Card> = [];
        for (let i = 0; i < cards.length; i++) {
            threeOfKindPositions.push(i);
            for (let j = 0; j < cards.length; j++) {
                if (cards[i].getName() === cards[j].getName() && i !== j) {
                    threeOfKindPositions.push(j);
                }

            }
            if (threeOfKindPositions.length >= 3) {
                break;
            }
            threeOfKindPositions = [];
        }

        if (threeOfKindPositions.length !== 3) {
            return false;
        }
        for (let i = 0; i < cards.length; i++) {
            if (threeOfKindPositions[0] !== i &&
                threeOfKindPositions[1] !== i &&
                threeOfKindPositions[2] !== i) {
                otherTwoCards.push(cards[i]);
            }
        }

        return otherTwoCards[0].getName() === otherTwoCards[1].getName();
    }

    private static isStraight(cards: Array<Card>): boolean {
        const cardsCopy = cards.slice();
        let straightElementsNumber = 1;
        cardsCopy.sort((el1: Card, el2: Card) => {
            if (el1.getValue() < el2.getValue()) {
                return -1;
            } else if (el1.getValue() > el2.getValue()) {
                return 1;
            } else {
                return 0;
            }
        });

        for (let i = 0; i < (cardsCopy.length - 1); i++) {
            if (cardsCopy[i].getValue() === (cardsCopy[i + 1].getValue() - 1)) {
                straightElementsNumber++;
            }
        }

        if (cardsCopy[0].getValue() === 1 &&
            cardsCopy[1].getValue() === 10 &&
            cardsCopy[2].getValue() === 11 &&
            cardsCopy[3].getValue() === 12 &&
            cardsCopy[4].getValue() === 13) {
            return true;
        }

        return straightElementsNumber === 5;
    }

    private static isFlush(cards: Array<Card>): boolean {
        for (let i = 0; i < cards.length; i++) {
            if (cards[0].getColor() !== cards[i].getColor()) {
                return false;
            }
        }
        return true;
    }

    private static isStraightFlush(cards: Array<Card>): boolean {
        return this.isStraight(cards) && this.isFlush(cards);
    }

    private static isRoyalFlush(cards: Array<Card>): boolean {
        const cardsCopy = cards.slice();
        cardsCopy.sort((el1: Card, el2: Card) => {
            if (el1.getValue() < el2.getValue()) {
                return -1;
            } else if (el1.getValue() > el2.getValue()) {
                return 1;
            } else {
                return 0;
            }
        });

        return cardsCopy[0].getValue() === 1 &&
            cardsCopy[1].getValue() === 10 &&
            cardsCopy[2].getValue() === 11 &&
            cardsCopy[3].getValue() === 12 &&
            cardsCopy[4].getValue() === 13 &&
            this.isFlush(cards);
    }

    private static isFourOfAKind(cards: Array<Card>): boolean {
        let sameCardsAmount = 1;
        for (let i = 0; i < cards.length; i++) {
            for (let j = 0; j < cards.length; j++) {
                if (cards[i].getName() === cards[j].getName() && i !== j) {
                    sameCardsAmount++;
                }

                if (sameCardsAmount === 4) {
                    return true;
                }
            }
            sameCardsAmount = 1;
        }
        return false;
    }

    private static getCombinationPosition(cards: Array<Card>,
                                          combinationName: VideoPokerCardCombinationName): Array<number> {
        if (cards.length !== 5) {
            throw new Error("Wrong amount of cards send!");
        }

        switch (combinationName) {
            case VideoPokerCardCombinationName.NOTHING:
                return [];
            case VideoPokerCardCombinationName.JACKS_OR_BETTER:
                return this.getJacksOrBetterPosition(cards);
            case VideoPokerCardCombinationName.TWO_PAIRS:
                return this.getTwoPairsPosition(cards);
            case VideoPokerCardCombinationName.THREE_OF_A_KIND:
                return this.getThreeOfAKindPosition(cards);
            case VideoPokerCardCombinationName.STRAIGHT:
                return [0, 1, 2, 3, 4];
            case VideoPokerCardCombinationName.FLUSH:
                return [0, 1, 2, 3, 4];
            case VideoPokerCardCombinationName.FULL_HOUSE:
                return [0, 1, 2, 3, 4];
            case VideoPokerCardCombinationName.FOUR_OF_A_KIND:
                return this.getFourOfAKindPosition(cards);
            case VideoPokerCardCombinationName.STRAIGHT_FLUSH:
                return [0, 1, 2, 3, 4];
            case VideoPokerCardCombinationName.ROYAL_FLUSH:
                return [0, 1, 2, 3, 4];
            default:
                throw new Error("Combination name not exist!");
        }
    }

    private static getJacksOrBetterPosition(cards: Array<Card>): Array<number> {
        for (let i = 0; i < cards.length; i++) {
            for (let j = 0; j < cards.length; j++) {
                if (i !== j &&
                    cards[i].getName() === cards[j].getName() &&
                    (
                        cards[j].getName() === CardName.JACK ||
                        cards[j].getName() === CardName.QUEEN ||
                        cards[j].getName() === CardName.KING ||
                        cards[j].getName() === CardName.ACE)) {
                    return [i, j];
                }
            }
        }
        throw new Error("Jacks Or Better Combination does not exist in given cards");
    }

    private static getTwoPairsPosition(cards: Array<Card>): Array<number> {
        const positionIndexes: Array<number> = [];
        for (let i = 0; i < cards.length; i++) {
            for (let j = 0; j < cards.length; j++) {
                if (i !== j &&
                    cards[i].getName() === cards[j].getName()) {
                    positionIndexes.push(i);
                    positionIndexes.push(j);
                    break;
                }
            }
            if (positionIndexes.length === 2) {
                break;
            }
        }

        for (let i = 0; i < cards.length; i++) {
            for (let j = 0; j < cards.length; j++) {
                if (i !== j &&
                    cards[i].getName() === cards[j].getName() &&
                    i !== positionIndexes[0] &&
                    i !== positionIndexes[1] &&
                    j !== positionIndexes[0] &&
                    j !== positionIndexes[1]) {
                    positionIndexes.push(i);
                    positionIndexes.push(j);
                    break;
                }
            }
            if (positionIndexes.length === 4) {
                break;
            }
        }

        return positionIndexes;
    }

    private static getThreeOfAKindPosition(cards: Array<Card>): Array<number> {
        let positionIndexes: Array<number> = [];
        for (let i = 0; i < cards.length; i++) {
            positionIndexes.push(i);
            for (let j = 0; j < cards.length; j++) {
                if (cards[i].getName() === cards[j].getName() && i !== j) {
                    positionIndexes.push(j);
                }

                if (positionIndexes.length === 3) {
                    return positionIndexes;
                }
            }
            positionIndexes = [];
        }
        return [];
    }

    private static getFourOfAKindPosition(cards: Array<Card>): Array<number> {
        let positionIndexes: Array<number> = [];
        for (let i = 0; i < cards.length; i++) {
            positionIndexes.push(i);
            for (let j = 0; j < cards.length; j++) {
                if (cards[i].getName() === cards[j].getName() && i !== j) {
                    positionIndexes.push(j);
                }

                if (positionIndexes.length === 4) {
                    return positionIndexes;
                }
            }
            positionIndexes = [];
        }
        return [];
    }

    private constructor() {

    }
}



// WEBPACK FOOTER //
// ./src/app/CardCombinationDetector.ts